import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { People as PeopleIcon, Person as PersonIcon, Link as LinkIcon } from '@mui/icons-material';
import { AgentInstance } from './hooks/useAIObservatory';

interface AgentInstancesPanelProps {
  instances: AgentInstance[];
}

export const AgentInstancesPanel: React.FC<AgentInstancesPanelProps> = ({ instances }) => {
  // Group instances by user
  const instancesByUser = instances.reduce((acc, instance) => {
    if (!acc[instance.userId]) {
      acc[instance.userId] = [];
    }
    acc[instance.userId].push(instance);
    return acc;
  }, {} as Record<string, AgentInstance[]>);

  const totalUsers = Object.keys(instancesByUser).length;

  return (
    <Card>
      <CardHeader
        title={<><PeopleIcon /> Agent Instances</>}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label={`${instances.length} instances`} color="primary" />
            <Chip label={`${totalUsers} users`} color="secondary" />
          </Box>
        }
      />
      <CardContent>
        {instances.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">No agent instances found</Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
            {Object.entries(instancesByUser).map(([userId, userInstances]) => (
              <Box key={userId} sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <PersonIcon color="primary" />
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    User {userId.substring(0, 12)}...
                  </Typography>
                  <Chip label={`${userInstances.length} instances`} size="small" />
                </Box>

                <List>
                  {userInstances.map((instance) => (
                    <ListItem key={instance.id}>
                      <ListItemText
                        primary={instance.name}
                        secondary={
                          <>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', my: 1 }}>
                              {instance.applicationKey && (
                                <Chip label={`App: ${instance.applicationKey}`} color="primary" variant="outlined" size="small" />
                              )}
                              {instance.featureKey && (
                                <Chip label={`Feature: ${instance.featureKey}`} color="info" variant="outlined" size="small" />
                              )}
                              {instance.providerKey && (
                                <Chip label={`Provider: ${instance.providerKey}`} color="secondary" variant="outlined" size="small" />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <LinkIcon sx={{ fontSize: 12 }} />
                              External ID: {instance.externalId}
                            </Typography>
                            {instance.assistantId && (
                              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                <LinkIcon sx={{ fontSize: 12 }} />
                                Config ID: {instance.assistantId}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
